import React, { memo, useCallback, useMemo, useRef, useState } from 'react';
import cn from 'classnames';

import { Icons } from '@components/common';

import { useOnClickOutsideSelect } from '@hooks/utils/useOnClickOutSide';

import { IFlatMoTreeNode, ITreeCheckboxOption } from '@interfaces/components/tree';

import { SELECT_TREE_ALL_CHECKED } from '@components/common/FlatTree/constants';

import TreeSelectItemsList from './TreeSelectItemsList';

import styles from './TreeSelect.module.scss';

interface ITreeSelectProps {
	data: ITreeCheckboxOption[];
	selectTree: (item: IFlatMoTreeNode, selected: string, isChecked: boolean) => void;
	node: IFlatMoTreeNode;
	isSingleSelectMode?: boolean;
	width?: string;
}

const TreeSelect = ({
	data = [],
	node,
	selectTree,
	width = '100px',
	isSingleSelectMode,
}: ITreeSelectProps) => {
	const menuRef = useRef<HTMLDivElement | null>(null);

	const [showMenu, setShowMenu] = useState(false);

	const isDisabled = isSingleSelectMode && node.checkboxList.length === 1 && node.checkboxList.length === 1;

	const wrapperClassNames = cn(styles.wrapper, {
		[styles.opened]: showMenu,
		[styles.disabled]: isDisabled,
	});

	const placeholder = cn(styles.select, {
		[styles.openSelect]: showMenu && !isSingleSelectMode,
	});

	const toggleShowMenu = (e: React.SyntheticEvent) => {
		if (isDisabled) return;

		e.stopPropagation();
		setShowMenu((prev) => !prev);
	};

	const closeShowMenu = useCallback(() => setShowMenu(false), []);

	useOnClickOutsideSelect([menuRef], () => {
		return showMenu && closeShowMenu();
	});

	const checkedLength = useMemo(() => data.filter((item) => item.isChecked).length, [data]);
	const cleared = useMemo(() => data.filter((item) => !!item.value), [data]);

	const isAllChecked = data.length === checkedLength && checkedLength !== 0;
	const isSemiChecked = checkedLength !== data.length && checkedLength !== 0;

	const selectText = useMemo(() => {
		return isAllChecked && !isSingleSelectMode
			? SELECT_TREE_ALL_CHECKED
			: cleared.filter(item => item.isChecked).map(item => item.value).join(', ') || SELECT_TREE_ALL_CHECKED;
	}, [isAllChecked, cleared, isSingleSelectMode]);

	const list = isSingleSelectMode
		? cleared
		: [{ value: SELECT_TREE_ALL_CHECKED, isChecked: isAllChecked, isSelected: true }, ...cleared];

	const handleChange = ({
		name,
		checked,
	}: { name: string; checked: boolean }) => {
		selectTree(node, name, checked);
	};

	return (
		<div
			className={wrapperClassNames}
			onClick={toggleShowMenu}
			style={{ width }}
		>
			<div
				ref={menuRef}
				className={styles.content}
			>
				<div
					className={placeholder}
				>
					<span className={styles.placeholderText}>{selectText}</span>
					<span className={styles.chevronWrapper}>
						{showMenu ? Icons.chevronUp() : Icons.chevronDown()}
					</span>
				</div>
				{showMenu && !!cleared.length &&
					<TreeSelectItemsList
						handleChange={handleChange}
						isSemiChecked={isSemiChecked}
						isSingleSelectMode={isSingleSelectMode}
						list={list}
					/>
				}
			</div>
		</div>
	);
};

export default memo(TreeSelect);
